import React, { useState } from "react";
import { Button, CircularProgress as MUIProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { styled } from "@mui/material/styles";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { Moment } from "moment";

import { RITHMIC_API_URLS, RithmicAPI } from "../../api/rithmic";
import { ContainerBg } from "../../useGeneralStyles";
import { DATE_FORMAT, DATES_VALUE } from "../../constants";
import SelectByDateWithCustomPeriod from "../../components/ui/SelectByDateWithCustomPeriod";
import { processAndDownloadCSV } from "./utils";
import UploadHistoryData from "./components/UploadHistoryData";

import s from "./styles.module.css";

interface StyledPickersDayProps extends PickersDayProps<Moment> {
  isMissedDay: boolean;
  isFutureDate: boolean;
}

const StyledPickersDay = styled(PickersDay)<StyledPickersDayProps>(
  ({ isMissedDay, isFutureDate }) => ({
    ...(isMissedDay && { color: "#ff0000c4" }),
    ...(isFutureDate && { color: "rgba(0, 0, 0, 0.38)" }),
  })
);

const RithmicHistoricalOrders = (): React.ReactElement => {
  const [availableHistoricalDates, setAvailableHistoricalDates] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useQuery({
    queryKey: [RITHMIC_API_URLS.get_rithmic_available_historical_data],
    queryFn: async () => {
      const resposne = await RithmicAPI.getRithmicAvailableHistoricalDataRequest();
      setAvailableHistoricalDates(resposne.data.availableHistoricalDates);
      return resposne;
    },
  });

  const getReport = async (startDate: string, endDate: string) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const downloadHistoricalData = async (startDate: string, endDate: string) => {
    setProgress(0);
    setIsLoading(true);

    const query = new URLSearchParams({
      startDate,
      endDate,
    });

    RithmicAPI.getRithmicHistoricalDataRequest(query.toString(), {
      onDownloadProgress: (progressEvent) => {
        setProgress(progressEvent.loaded);
      },
    })
      .then((response) =>
        processAndDownloadCSV(response, startDate, endDate, setProgress, setIsLoading)
      )
      .catch((error) => console.error("Download error:", error));
  };

  return (
    <ContainerBg>
      <h2>Rithmic Historical Orders</h2>

      <div className={s.filterContainer}>
        <SelectByDateWithCustomPeriod
          excludeDates={[DATES_VALUE.today]}
          initialDateBy={DATES_VALUE.yesterday}
          getReport={getReport}
          setIsLoading={() => void null}
          setReport={() => void null}
          renderDay={(date, selectedDates, pickersDayProps) => {
            const renderDay = date?.format(DATE_FORMAT);
            const isFutureDay = date?.isAfter();
            const isToday = date?.isSame(new Date(), "day");
            const isAvailableDay = availableHistoricalDates.includes(renderDay as string);
            return (
              // @ts-ignore
              <StyledPickersDay
                disableMargin
                isMissedDay={!isAvailableDay}
                isFutureDate={isFutureDay || isToday}
                {...pickersDayProps}
              />
            );
          }}
        />

        <Button
          onClick={() => downloadHistoricalData(startDate, endDate)}
          endIcon={isLoading && <MUIProgress color="inherit" size={20} />}
          disabled={isLoading}
          className={s.downloadButton}
        >
          Download {!!progress && <>{Math.round(progress / 1024)} kb</>}
        </Button>
      </div>

      <UploadHistoryData />
    </ContainerBg>
  );
};

export default RithmicHistoricalOrders;
